import { Injectable, Injector } from '@angular/core';
import { ArboService } from './arbo.service';
import { HttpService } from './http.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  private type = 'Campaign';

  // Cache em memória por accountId
  private _listCache = new Map<string, { data: any; timestamp: number }>();
  private _dataCache = new Map<string, { data: any; timestamp: number }>();
  private _pendingRequests = new Map<string, Promise<any>>(); // Dedup requests em andamento
  private CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  startDate: any;
  endDate: any;
  sources = [];
  postsIds = [];
  data;

  campaignConf = [
    {
      startDate: '',
      endDate: '',
      posts: {},
      sources: [
        {
          networkName: 'Facebook',
          sourceID: [],
        },
        {
          networkName: 'Instagram',
          sourceID: [],
        },
        {
          networkName: 'Youtube',
          sourceID: [],
        },
      ],
    },
  ];

  nameCampaign: string;
  accountId;
  typeCampaign = 'Networks';

  constructor(
    private arboService: ArboService,
    private httpService: HttpService,
    private injector: Injector
  ) {}

  // Lazy-load SessionService para evitar dependencia circular
  private get sessionService(): any {
    const { SessionService } = require('./session.service');
    return this.injector.get(SessionService);
  }

  filterDate() {
    let data = this.postsIds.map((post) => {
      return new Date(post.createdDate).getTime();
    });

    let e = new Date(Math.max(...data));
    e.setDate(e.getDate() + 7);
    this.endDate = e.toISOString();
    this.startDate = new Date(Math.min(...data)).toISOString();
  }

  refreshSources() {

    while (this.sources.length > 0) {
      this.sources.pop();
    }

    this.campaignConf[0].sources.forEach((source) => {
      source.sourceID.forEach((sourceId) => {
        this.sources.push(sourceId);
      });
    });

  }

  async createCampaign() {
    const result = await this.arboService.create(this.type, {
      accountId: this.sessionService.account.id,
      type: this.typeCampaign,
      name: this.nameCampaign,
      startDate: this.startDate,
      endDate: this.endDate,
      postIds: this.postsIds.map(post => post.id),
      sectionId: this.sessionService.sectionId
    });
    this.invalidateCache(String(this.sessionService.account.id));
    return result;
  }

  async updateCampaign(campaignId: number | string) {
    const numericId = typeof campaignId === 'string' ? parseInt(campaignId, 10) : campaignId;
    const payload = {
      id: numericId,
      accountId: this.sessionService.account.id,
      name: this.nameCampaign,
      startDate: this.startDate,
      endDate: this.endDate,
      postIds: this.postsIds.map(post => post.id),
      sectionId: this.sessionService.sectionId
    };
    console.log('[CampaignService.updateCampaign] payload:', payload);
    const result = await this.arboService.update(this.type, payload);
    this.invalidateCache(String(this.sessionService.account.id));
    return result;
  }

  list(account) {
    const key = String(account);
    const cached = this._listCache.get(key);
    if (cached && (Date.now() - cached.timestamp < this.CACHE_TTL)) {
      return Promise.resolve(cached.data);
    }
    // Dedup: se já há uma request em andamento para a mesma chave, reutiliza
    if (this._pendingRequests.has(key)) {
      return this._pendingRequests.get(key);
    }
    const request = this.httpService.get(
      `${environment.arboUrl}/Campaign/account/${account}`
    ).then(data => {
      this._listCache.set(key, { data, timestamp: Date.now() });
      this._pendingRequests.delete(key);
      return data;
    }).catch(err => {
      this._pendingRequests.delete(key);
      throw err;
    });
    this._pendingRequests.set(key, request);
    return request;
  }

  invalidateCache(account?: string) {
    if (account) {
      this._listCache.delete(account);
      this._listCache.delete(`summary_${account}`);
    } else {
      this._listCache.clear();
    }
    this._dataCache.clear();
  }

  /**
   * Lista campanhas com métricas resumidas.
   * Endpoint: GET /Campaign/account/:accountId/summary
   * Reutiliza o mesmo padrão de cache/dedup de list().
   */
  listWithSummary(account): Promise<any> {
    const key = `summary_${account}`;
    const cached = this._listCache.get(key);
    if (cached && (Date.now() - cached.timestamp < this.CACHE_TTL)) {
      return Promise.resolve(cached.data);
    }
    if (this._pendingRequests.has(key)) {
      return this._pendingRequests.get(key);
    }
    const request = this.httpService.get(
      `${environment.arboUrl}/Campaign/account/${account}/summary`
    ).then(data => {
      this._listCache.set(key, { data, timestamp: Date.now() });
      this._pendingRequests.delete(key);
      return data;
    }).catch(err => {
      this._pendingRequests.delete(key);
      throw err;
    });
    this._pendingRequests.set(key, request);
    return request;
  }

  loadSpecialCampaign(accountId) {
    return this.httpService.get(
      `${environment.arboUrl}/Campaign/account/${accountId}/overview`
    );
  }

  loadCampaignMorePosts(nextPostsUrl: string) {
    if (!nextPostsUrl) {
      console.warn('❌ Nenhuma URL encontrada para carregar novos posts.');
      return;
    }

    return this.httpService.get(nextPostsUrl);
  }

  /**
   * Busca posts de uma campanha por rede social, com ordem e paginação
   * Endpoint: /Campaign/{id}/posts?limit=10&offset=0&order=DESC&network=youtube
   */
  loadCampaignPostsByNetwork(campaignId: number | string, network: string, limit = 10, offset = 0, order = 'DESC') {
    const url = `${environment.arboUrl}/Campaign/${campaignId}/posts?limit=${limit}&offset=${offset}&order=${order}&network=${network}`;
    return this.httpService.get(url);
  }

  getCampaignStatistics(accountId: string) {
    return this.httpService.get(`${environment.arboUrl}/Account/campaign-statistics?accountId=${accountId}`);
  }

  load(campaignId, sourceIds = [0]) {
    return this.httpService.get(`${environment.arboUrl}/Campaign/${campaignId}`);
  }

  async loadData(id) {
    const key = `data_${id}`;
    const cached = this._dataCache.get(key);
    if (cached && (Date.now() - cached.timestamp < this.CACHE_TTL)) {
      return cached.data;
    }
    // Dedup: reutiliza request em andamento
    if (this._pendingRequests.has(key)) {
      return this._pendingRequests.get(key);
    }
    const request = this.httpService.get(`${environment.arboUrl}/Campaign/${id}`)
      .then(data => {
        this._dataCache.set(key, { data, timestamp: Date.now() });
        this._pendingRequests.delete(key);
        return data;
      })
      .catch(err => {
        this._pendingRequests.delete(key);
        throw err;
      });
    this._pendingRequests.set(key, request);
    return request;
  }

  avgNet(postEids, names, aggregateFunc = 'AVG') {
    let data = {
      postIds: postEids,
      names: names,
      aggregateFunc: aggregateFunc,
    };
    return this.httpService.post(
      `${environment.arboUrl}/Insight/averagePost`,
      data
    );
  }

  best(postIds, insightVariables, from, to, sourceId = null) {
    let data = {
      postIds,
      insightVariables,
      from,
      to,
      sourceId,
    };
    return this.httpService.post(
      environment.arboUrl + '/' + this.type + '/bestPost',
      data
    );
  }

  setStartDate(data) {
    this.startDate = data;
    this.campaignConf.forEach((campaign) => (campaign.startDate = data));
  }
  setEndDate(data) {
    this.endDate = data;
    this.campaignConf.forEach((campaign) => (campaign.endDate = data));
  }
  addSource(sourceId) {
    this.campaignConf.forEach((campaign) => campaign.sources.push(sourceId));
  }
  removeSource(sourceId) {
    this.campaignConf.forEach((campaign) => {
      const idx = campaign.sources.indexOf(sourceId);
      if (idx > -1) campaign.sources.splice(idx, 1);
    });
  }
  
  /**
   * Encerra a campanha definindo endDate = hoje. Mantém os outros campos
   * intactos (não substitui name/startDate/postIds porque endpoint update
   * faz merge no arbojs).
   */
  async closeCampaign(campaignId: number | string) {
    const numericId = typeof campaignId === 'string' ? parseInt(campaignId, 10) : campaignId;
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      id: numericId,
      accountId: this.sessionService.account.id,
      endDate: today,
      sectionId: this.sessionService.sectionId,
    };
    const result = await this.arboService.update(this.type, payload);
    this.invalidateCache(String(this.sessionService.account.id));
    return result;
  }

  deleteCampaign(campaignId) {
    return this.arboService.delete(this.type, campaignId);
  }
  
  async getCrmCampaign(campaignId) {
    return await this.httpService.get(environment.arboUrl + '/' + this.type + '/'+ campaignId + '/loadCRM');
  }

  // connectNetwork(networkName) {
  //   switch (networkName) {
  //     case 'fb':
  //       this.fbService.login();
  //       break;

  //     default:
  //       break;
  //   }
  // }
}
